/**
 * API サービス - デバイス管理用
 *
 * API Gateway への HTTP リクエストを管理
 * Cognito ID Token を自動付与
 */

import { getSession, signOut } from './auth';

const API_BASE = process.env.REACT_APP_API_ENDPOINT;

/**
 * 認証付き fetch
 */
async function authFetch(path, options = {}) {
  let session;
  try {
    session = await getSession();
  } catch (err) {
    // セッション切れ → ログアウト
    signOut();
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  const url = `${API_BASE}${path}`;
  const headers = {
    'Content-Type': 'application/json',
    Authorization: session.idToken,
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    signOut();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || `API Error: ${response.status}`);
  }

  return data;
}

// ============================================================
// Device API
// ============================================================

export const deviceApi = {
  /** デバイス一覧 */
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authFetch(`/devices${query ? `?${query}` : ''}`);
  },

  /** デバイス詳細 */
  get: (thingName) => authFetch(`/devices/${encodeURIComponent(thingName)}`),

  /** デバイス更新 */
  update: (thingName, body) =>
    authFetch(`/devices/${encodeURIComponent(thingName)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  /** デバイス削除 */
  delete: (thingName) =>
    authFetch(`/devices/${encodeURIComponent(thingName)}`, {
      method: 'DELETE',
    }),

  /** 担当者の設定・解除（userId: null で解除。orgId は superadmin が他組織のメンバーを選ぶときのみ） */
  setAssignee: (thingName, userId, orgId) =>
    authFetch(`/devices/${encodeURIComponent(thingName)}/assignee`, {
      method: 'PUT',
      body: JSON.stringify(orgId ? { userId, orgId } : { userId }),
    }),

  /** デバイス検索 (Fleet Indexing) */
  search: (queryString, maxResults = 50) =>
    authFetch('/devices/search', {
      method: 'POST',
      body: JSON.stringify({ queryString, maxResults }),
    }),

  /** デバイスイベント履歴（接続/切断/Shadow更新） */
  listEvents: (thingName, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authFetch(`/devices/${encodeURIComponent(thingName)}/events${query ? `?${query}` : ''}`);
  },

  /** デバイスMQTTログ（CloudWatch Logs） */
  listLogs: (thingName, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authFetch(`/devices/${encodeURIComponent(thingName)}/logs${query ? `?${query}` : ''}`);
  },
};

// ============================================================
// Shadow API
// ============================================================

export const shadowApi = {
  /** Shadow 取得 */
  get: (thingName, shadowName = null) => {
    const path = shadowName
      ? `/shadows/${encodeURIComponent(thingName)}/${encodeURIComponent(shadowName)}`
      : `/shadows/${encodeURIComponent(thingName)}`;
    return authFetch(path);
  },

  /** Shadow 更新 (desired) */
  update: (thingName, desired, shadowName = null) => {
    const path = shadowName
      ? `/shadows/${encodeURIComponent(thingName)}/${encodeURIComponent(shadowName)}`
      : `/shadows/${encodeURIComponent(thingName)}`;
    return authFetch(path, {
      method: 'POST',
      body: JSON.stringify({ desired }),
    });
  },

  /** Named Shadow 一覧 */
  listNames: (thingName) =>
    authFetch(`/shadows/${encodeURIComponent(thingName)}/list`),
};

// ============================================================
// Provision API
// ============================================================

export const provisionApi = {
  /** 新規デバイス登録 */
  create: (body) =>
    authFetch('/Provision', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** 一括登録 */
  bulk: (devices) =>
    authFetch('/Provision/bulk', {
      method: 'POST',
      body: JSON.stringify({ devices }),
    }),

  /** ライフサイクルステータス更新 */
  updateStatus: (thingName, status, notes = '') =>
    authFetch(`/Provision/${encodeURIComponent(thingName)}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, notes }),
    }),

  /** 証明書ローテーション */
  rotateCert: (thingName) =>
    authFetch(`/Provision/${encodeURIComponent(thingName)}/rotate-cert`, {
      method: 'POST',
    }),

  /** Amazon Root CA証明書取得 */
  getRootCa: () => authFetch('/Provision/root-ca'),
};

// ============================================================
// Group API
// ============================================================

export const groupApi = {
  /** グループ一覧 */
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authFetch(`/groups${query ? `?${query}` : ''}`);
  },

  /** グループ詳細 */
  get: (groupName) => authFetch(`/groups/${encodeURIComponent(groupName)}`),

  /** グループ作成 */
  create: (body) =>
    authFetch('/groups', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** グループ削除 */
  delete: (groupName) =>
    authFetch(`/groups/${encodeURIComponent(groupName)}`, {
      method: 'DELETE',
    }),

  /** グループ内 Thing 一覧 */
  listThings: (groupName, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authFetch(
      `/groups/${encodeURIComponent(groupName)}/things${query ? `?${query}` : ''}`
    );
  },

  /** Thing をグループに追加 */
  addThing: (groupName, thingName) =>
    authFetch(
      `/groups/${encodeURIComponent(groupName)}/things/${encodeURIComponent(thingName)}`,
      { method: 'PUT' }
    ),

  /** Thing をグループから除外 */
  removeThing: (groupName, thingName) =>
    authFetch(
      `/groups/${encodeURIComponent(groupName)}/things/${encodeURIComponent(thingName)}`,
      { method: 'DELETE' }
    ),
};

// ============================================================
// Thing Type API
// ============================================================

export const thingTypeApi = {
  /** Thing Type 一覧 */
  list: () => authFetch('/thing-types'),
};

// ============================================================
// Firmware API
// ============================================================

export const firmwareApi = {
  /** ファームウェア一覧 */
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authFetch(`/firmware${query ? `?${query}` : ''}`);
  },

  /** ファームウェア詳細 */
  get: (version) => authFetch(`/firmware/${encodeURIComponent(version)}`),

  /** ファームウェア新規登録 */
  create: (body) =>
    authFetch('/firmware', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** ファームウェア更新 */
  update: (version, body) =>
    authFetch(`/firmware/${encodeURIComponent(version)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  /** ファームウェア削除 */
  delete: (version) =>
    authFetch(`/firmware/${encodeURIComponent(version)}`, {
      method: 'DELETE',
    }),

  /** S3アップロードURL取得 */
  getUploadUrl: (version, filename, contentType) =>
    authFetch(`/firmware/${encodeURIComponent(version)}/upload-url`, {
      method: 'POST',
      body: JSON.stringify({ filename, contentType }),
    }),

  /** ファイルをS3にアップロード */
  uploadFile: async (version, file) => {
    const { uploadUrl, headers } = await firmwareApi.getUploadUrl(
      version,
      file.name,
      file.type || 'application/octet-stream'
    );

    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers,
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status}`);
    }

    // アップロード完了後、チェックサム計算を実行
    const confirmResult = await firmwareApi.confirmUpload(version);

    return {
      success: true,
      filename: file.name,
      size: file.size,
      checksum: confirmResult.checksum,
      checksumAlgorithm: confirmResult.checksumAlgorithm,
    };
  },

  /** アップロード完了確認 & チェックサム計算 */
  confirmUpload: (version) =>
    authFetch(`/firmware/${encodeURIComponent(version)}/confirm-upload`, {
      method: 'POST',
    }),

  /** 最新バージョンチェック（プル型OTA用） */
  getLatest: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authFetch(`/firmware/latest${query ? `?${query}` : ''}`);
  },

  /** 更新完了報告（プル型OTA用） */
  report: (body) =>
    authFetch('/firmware/report', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** デバイス別FWバージョン一覧 */
  listDevices: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authFetch(`/devices/firmware${query ? `?${query}` : ''}`);
  },
};

// ============================================================
// OTA API
// ============================================================

export const otaApi = {
  /** OTAジョブ一覧 */
  list: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authFetch(`/ota/jobs${query ? `?${query}` : ''}`);
  },

  /** OTAジョブ詳細 */
  get: (jobId) => authFetch(`/ota/jobs/${encodeURIComponent(jobId)}`),

  /** OTAジョブ作成 */
  create: (body) =>
    authFetch('/ota/jobs', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** OTAジョブキャンセル */
  cancel: (jobId, comment = '', force = false) =>
    authFetch(`/ota/jobs/${encodeURIComponent(jobId)}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ comment, force }),
    }),

  /** ジョブ実行状況取得 */
  getExecutions: (jobId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authFetch(
      `/ota/jobs/${encodeURIComponent(jobId)}/executions${query ? `?${query}` : ''}`
    );
  },
};

// ============================================================
// Build Pipeline API
// ============================================================

export const buildApi = {
  // ============================================================
  // S3 テスト結果ブラウジング
  // ============================================================

  /** テスト実行一覧 */
  listTestRuns: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authFetch(`/builds/test-runs${query ? `?${query}` : ''}`);
  },

  /** テスト実行開始（SSM経由でEC2上で実行） */
  startTestRun: (body = {}) =>
    authFetch('/builds/test-runs', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** テスト実行中止 */
  stopTestRun: (testId) =>
    authFetch(`/builds/test-runs/${encodeURIComponent(testId)}/stop`, {
      method: 'POST',
    }),

  /** スケジュールテスト一覧 */
  listScheduledTests: () => authFetch('/builds/schedules'),

  /** テストスケジュール設定 */
  scheduleTest: (body) =>
    authFetch('/builds/schedules', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** スケジュールキャンセル */
  cancelScheduledTest: (scheduleId) =>
    authFetch(`/builds/schedules/${encodeURIComponent(scheduleId)}`, {
      method: 'DELETE',
    }),

  /** テスト実行詳細 */
  getTestRun: (testId) => authFetch(`/builds/test-runs/${encodeURIComponent(testId)}`),

  /** テスト実行ファイル取得 */
  getTestRunFile: (testId, fileName, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authFetch(`/builds/test-runs/${encodeURIComponent(testId)}/files/${encodeURIComponent(fileName)}${query ? `?${query}` : ''}`);
  },

  /** テスト動画の署名付きURL取得 */
  getTestVideoUrl: (testId, fileName = 'simulation.mp4') =>
    authFetch(`/builds/test-runs/${encodeURIComponent(testId)}/files/${encodeURIComponent(fileName)}?presigned=true`),

  /** テストログの署名付きURL取得（ダウンロード用） */
  getTestLogUrl: (testId) =>
    authFetch(`/builds/test-runs/${encodeURIComponent(testId)}/files/test.log?presigned=true`),

  /** スクリーンショット一覧取得 */
  listScreenshots: (testId) =>
    authFetch(`/builds/test-runs/${encodeURIComponent(testId)}/screenshots`),

  /** スクリーンショット取得（署名付きURL） */
  getScreenshotUrl: (testId, fileName) =>
    authFetch(`/builds/test-runs/${encodeURIComponent(testId)}/screenshots/${encodeURIComponent(fileName)}`),

  /** カバレッジレポート取得 */
  getCoverageReport: (testId) =>
    authFetch(`/builds/test-runs/${encodeURIComponent(testId)}/coverage`),

  /** テスト実行ステータス取得（リアルタイム監視用） */
  getTestRunStatus: (testId) =>
    authFetch(`/builds/test-runs/${encodeURIComponent(testId)}/status`),

  /** テストログストリーミング取得 */
  getTestRunLogStream: (testId, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authFetch(`/builds/test-runs/${encodeURIComponent(testId)}/logs/stream${query ? `?${query}` : ''}`);
  },

  // ============================================================
  // EC2 インスタンス監視・制御
  // ============================================================

  /** EC2インスタンスステータス取得 */
  getEC2Status: () => authFetch('/builds/ec2/status'),

  /** EC2メトリクス取得（グラフ用） */
  getEC2Metrics: (period = '1h') => authFetch(`/builds/ec2/metrics?period=${period}`),

  /** Gitブランチ一覧取得 */
  getGitBranches: () => authFetch('/builds/git/branches'),

  /** EC2インスタンス起動 */
  startEC2: (body = {}) =>
    authFetch('/builds/ec2/start', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** EC2インスタンス停止 */
  stopEC2: (body = {}) =>
    authFetch('/builds/ec2/stop', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** EC2ライフサイクル状態取得（タイマー情報含む） */
  getEC2Lifecycle: () => authFetch('/builds/ec2/lifecycle'),

  // ============================================================
  // パイプラインRun管理
  // ============================================================

  /** パイプライン実行状態チェック（開始可否判定） */
  getPipelineStatus: () => authFetch('/builds/pipelines/status'),

  /** パイプライン一覧取得 */
  listPipelines: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authFetch(`/builds/pipelines${query ? `?${query}` : ''}`);
  },

  /** パイプライン開始 */
  createPipeline: (body) =>
    authFetch('/builds/pipelines', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** パイプライン詳細取得 */
  getPipeline: (pipelineId) =>
    authFetch(`/builds/pipelines/${encodeURIComponent(pipelineId)}`),

  /** パイプライン承認 */
  approvePipeline: (pipelineId, approved = true, comment = '', releaseVersion = undefined) =>
    authFetch(`/builds/pipelines/${encodeURIComponent(pipelineId)}/approve`, {
      method: 'POST',
      body: JSON.stringify({ approved, comment, ...(releaseVersion ? { releaseVersion } : {}) }),
    }),

  /** パイプライン中止 */
  stopPipeline: (pipelineId) =>
    authFetch(`/builds/pipelines/${encodeURIComponent(pipelineId)}/stop`, {
      method: 'POST',
    }),

  /** パイプラインOTAテスト再実行 */
  retryPipelineOtaTest: (pipelineId) =>
    authFetch(`/builds/pipelines/${encodeURIComponent(pipelineId)}/retry-ota`, {
      method: 'POST',
    }),

  // ============================================================
  // Artifact Check（ビルド成果物検証）API
  // ============================================================

  /** アーティファクトチェック一覧取得 */
  listArtifactChecks: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authFetch(`/builds/artifact-checks${query ? `?${query}` : ''}`);
  },

  /** アーティファクトチェック詳細取得 */
  getArtifactCheck: (checkId) =>
    authFetch(`/builds/artifact-checks/${encodeURIComponent(checkId)}`),

  // ============================================================
  // HiLS (GitHub Actions) API
  // ============================================================

  /** HiLS実行一覧取得 */
  listHilsRuns: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authFetch(`/builds/hils/runs${query ? `?${query}` : ''}`);
  },

  /** HiLS実行トリガー */
  triggerHilsRun: (body) =>
    authFetch('/builds/hils/runs', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** HiLS実行詳細取得 */
  getHilsRun: (runId) =>
    authFetch(`/builds/hils/runs/${encodeURIComponent(runId)}`),

  /** HiLS実行ログ取得 */
  getHilsRunLogs: (runId) =>
    authFetch(`/builds/hils/runs/${encodeURIComponent(runId)}/logs`),

  /** HiLS実行キャンセル */
  cancelHilsRun: (runId) =>
    authFetch(`/builds/hils/runs/${encodeURIComponent(runId)}/cancel`, {
      method: 'POST',
    }),

  /** HiLS OTAテストキャンセル */
  cancelHilsOtaTest: (jobId) =>
    authFetch(`/builds/hils/ota-test/${encodeURIComponent(jobId)}/cancel`, {
      method: 'POST',
    }),

  /** HiLSデバイス一覧・接続状態取得 */
  getHilsDevices: () => authFetch('/builds/hils/devices'),

  /** OTAテストのみ実行（HiLSビルドスキップ） */
  triggerOtaTestOnly: (body) =>
    authFetch('/builds/hils/ota-only', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** HiLS設定取得 */
  getHilsConfig: () => authFetch('/builds/hils/config'),

  /** HiLS設定更新 */
  updateHilsConfig: (body) =>
    authFetch('/builds/hils/config', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  // --- HiLS OTAテスト ---

  /** HiLSテスト一覧取得 */
  listHilsTests: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return authFetch(`/builds/hils/tests${query ? `?${query}` : ''}`);
  },

  /** HiLSテスト詳細取得 */
  getHilsTest: (jobId) =>
    authFetch(`/builds/hils/tests/${encodeURIComponent(jobId)}`),

  /** HiLS OTAテスト作成 */
  createHilsOtaTest: (body) =>
    authFetch('/builds/hils/ota-test', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

// ============================================================
// Version API (Thing バージョン管理)
// ============================================================

export const versionApi = {
  /** Thing バージョン一覧 */
  list: () => authFetch('/versions'),

  /** Thing バージョン詳細 */
  get: (thingName) => authFetch(`/versions/${encodeURIComponent(thingName)}`),
};

// ============================================================
// Flight Log API (フライトログ ulog の保管)
// ============================================================

/** ファイルの SHA-256（hex）をブラウザで計算 */
async function sha256Hex(file) {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** presigned PUT を XHR で実行（進捗コールバック付き） */
function putWithProgress(url, headers, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    Object.entries(headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 upload failed: ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('S3 upload failed: network error'));
    xhr.send(file);
  });
}

export const flightLogApi = {
  /** 一覧（既定は stored のみ、新しい順）。params: thingName, from, to, limit, nextToken, status */
  list: (params = {}) => {
    const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''));
    const query = new URLSearchParams(clean).toString();
    return authFetch(`/flight-logs${query ? `?${query}` : ''}`);
  },

  /** 詳細 */
  get: (logId) => authFetch(`/flight-logs/${encodeURIComponent(logId)}`),

  /** presigned GET URL 取得（生の .ulg をそのまま返す） */
  getDownloadUrl: (logId) => authFetch(`/flight-logs/${encodeURIComponent(logId)}/download-url`),

  /** ブラウザでダウンロードを開始する */
  download: async (logId) => {
    const { downloadUrl, filename } = await flightLogApi.getDownloadUrl(logId);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    return { filename };
  },

  /** presigned PUT URL 取得 */
  getUploadUrl: (body) =>
    authFetch('/flight-logs/upload-url', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** アップロード完了確認（ulog 検証・開始時刻確定） */
  complete: (logId) =>
    authFetch(`/flight-logs/${encodeURIComponent(logId)}/complete`, {
      method: 'POST',
    }),

  /**
   * .ulg を 1 本アップロードする
   * @param {string} thingName
   * @param {File} file
   * @param {(stage: string, percent?: number) => void} onProgress
   */
  uploadFile: async (thingName, file, onProgress = () => {}) => {
    onProgress('hashing');
    const sha256 = await sha256Hex(file);

    onProgress('requesting');
    const { logId, uploadUrl, headers } = await flightLogApi.getUploadUrl({
      thingName,
      filename: file.webkitRelativePath || file.name,
      sizeBytes: file.size,
      sha256,
      source: 'manual',
    });

    onProgress('uploading', 0);
    await putWithProgress(uploadUrl, headers, file, (p) => onProgress('uploading', p));

    onProgress('verifying');
    const result = await flightLogApi.complete(logId);
    onProgress('done', 100);
    return result;
  },
};
